import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  CalendarDays,
  User,
  Tag,
  Eye,
  FileText,
  Link2,
  Pencil,
  ExternalLink,
  Info,
  Clipboard,
  Download,
} from 'lucide-react';
import { useLegalFoundation } from '@/lib/api/hooks/legalFoundation';
import { Loader } from '@/components/ui/Loader';
import { ActionDropdown, type DropdownAction } from '@/components/ui';

const LegalFoundationDetailPage: React.FC = () => {
  const { id: legalFoundationId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: legalFoundationData, isLoading, error } = useLegalFoundation(legalFoundationId || '');
  const legalFoundation = legalFoundationData?.data;

  if (!legalFoundationId) {
    return (
      <div className="text-center p-8 bg-white rounded-lg">
        <h2 className="text-xl font-bold">Legal Foundation ID missing</h2>
        <button onClick={() => navigate('/admin/legal-foundations')} className="mt-4 text-[#681628] hover:underline">
          &larr; Back to Legal Foundations
        </button>
      </div>
    );
  }

  const handleEdit = () => {
    navigate(`/admin/legal-foundations/${legalFoundationId}/edit`);
  };

  const getDropdownActions = (): DropdownAction[] => [
    {
      label: 'Edit',
      onClick: (e) => {
        e.stopPropagation();
        handleEdit();
      }
    },
    {
      label: legalFoundation?.status === 'PUBLISH' ? 'Published' : 'Draft',
      onClick: () => {},
      disabled: true
    }
  ];

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return '-';
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
    } catch {
      return '-';
    }
  };

  const formatDateTime = (dateString?: string | null) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return '-';
      return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '-';
    }
  };

  const formatCategory = (category: string) => {
    return category
      .split(/[_-]/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-10">
        <Loader size="md" variant="wine" message="Loading Legal Foundation..." />
      </div>
    );
  }

  if (error || !legalFoundation) {
    return (
      <div className="text-center p-8 bg-white rounded-lg">
        <h2 className="text-xl font-bold">Legal Foundation not found</h2>
        <p className="text-sm text-gray-500 mt-2">{error?.message || 'The legal foundation could not be found'}</p>
        <button onClick={() => navigate('/admin/legal-foundations')} className="mt-4 text-[#681628] hover:underline">
          &larr; Back to Legal Foundations
        </button>
      </div>
    );
  }

  const isExternal = Boolean(legalFoundation.isExternalSource || legalFoundation.externalLink);
  const docUrl = legalFoundation.legalFoundationUrl || legalFoundation.externalLink || '';
  const fileExtension = legalFoundation.legalFoundationUrl
    ? legalFoundation.legalFoundationUrl.split('?')[0].split('.').pop()?.toUpperCase() || 'FILE'
    : 'LINK';
  const statusLabel = legalFoundation.status === 'PUBLISH' ? 'Published' : 'Draft';
  const statusClass = legalFoundation.status === 'PUBLISH'
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : 'bg-blue-50 text-blue-700 border-blue-200';
  const categoryLabel = formatCategory(legalFoundation.legalFoundationCategory);

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="text-sm text-gray-500">
        <span className="cursor-pointer hover:underline" onClick={() => navigate('/admin/legal-foundations')}>Legal Foundations</span>
        <span className="mx-2">&gt;</span>
        <span className="font-medium text-gray-700">{legalFoundation.title}</span>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-6 items-start">
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
              <div className="space-y-3 min-w-0">
                <h1 className="text-2xl font-semibold text-gray-900 break-words">{legalFoundation.title}</h1>
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`inline-flex items-center gap-1 rounded-md border px-3 py-1 text-xs font-medium ${statusClass}`}>
                    <span className={`h-2 w-2 rounded-md ${legalFoundation.status === 'PUBLISH' ? 'bg-emerald-500' : 'bg-blue-500'}`} />
                    {statusLabel}
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-md border border-violet-200 bg-violet-50 px-3 py-1 text-xs font-medium text-violet-700">
                    {categoryLabel}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {/* <button
                  type="button"
                  onClick={handleEdit}
                  className="inline-flex items-center gap-2 rounded-md bg-wine-100 px-4 py-2 text-sm font-medium whitespace-nowrap text-white hover:bg-wine-200"
                >
                  <Pencil className="w-4 h-4" />
                  Edit LegalFoundation
                </button> */}
             
                <ActionDropdown id={legalFoundationId} actions={getDropdownActions()} />
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 border-b border-gray-200 pb-6">
              <div className="space-y-1">
                <p className="text-xs text-gray-500 inline-flex items-center gap-1"><User className="w-3.5 h-3.5" /> Author</p>
                <p className="text-sm font-medium text-gray-900">{legalFoundation.createdByName || legalFoundation.createdBy}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-gray-500 inline-flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" /> Created</p>
                <p className="text-sm font-medium text-gray-900">{formatDate(legalFoundation.createdAt)}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-gray-500 inline-flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" /> Published</p>
                <p className="text-sm font-medium text-gray-900">{formatDate(legalFoundation.datePublished)}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-gray-500 inline-flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> Document Type</p>
                <p className="text-sm font-medium text-gray-900">{isExternal ? 'External Link' : fileExtension}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 pt-6">
              <div className="space-y-1">
                <p className="text-xs text-gray-500 inline-flex items-center gap-1"><Tag className="w-3.5 h-3.5" /> Category</p>
                <p className="text-sm font-medium text-gray-900">{categoryLabel}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-gray-500 inline-flex items-center gap-1"><Link2 className="w-3.5 h-3.5" /> Source</p>
                <p className="text-sm font-medium text-gray-900">{isExternal ? 'External' : 'Internal File'}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-gray-500 inline-flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> Visibility</p>
                <p className="text-sm font-medium text-gray-900">{legalFoundation.status === 'PUBLISH' ? 'Public' : 'Draft only'}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-gray-500 inline-flex items-center gap-1"><Clipboard className="w-3.5 h-3.5" /> Document ID</p>
                <p className="text-sm font-medium text-gray-900 break-all">{legalFoundation.legalFoundationId}</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-3">
            <h3 className="text-lg font-semibold text-gray-900 inline-flex items-center gap-2">
              <Info className="w-4 h-4" /> Description
            </h3>
            <p className="text-gray-700 leading-7 text-base">{legalFoundation.description || '-'}</p>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 inline-flex items-center gap-2">
              <Eye className="w-4 h-4" /> Analytics Overview
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Views</p>
                  <p className="text-3xl font-semibold text-gray-900 mt-1">{legalFoundation.totalViews || 0}</p>
                  <p className="text-xs text-gray-500 mt-1">All time</p>
                </div>
                <div className="h-14 w-14 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Eye className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
              <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Downloads</p>
                  <p className="text-3xl font-semibold text-gray-900 mt-1">{legalFoundation.totalDownloads || 0}</p>
                  <p className="text-xs text-gray-500 mt-1">All time</p>
                </div>
                <div className="h-14 w-14 rounded-full bg-blue-100 flex items-center justify-center">
                  <Download className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-3 min-w-0">
                <div className="h-12 w-12 rounded-lg bg-[#951f3910] text-wine-100 flex items-center justify-center shrink-0">
                  {isExternal ? <Link2 className="w-5 h-5" /> : <FileText className="w-5 h-5" />}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900">{isExternal ? 'External Document Link' : 'LegalFoundation Document'}</p>
                  <p className="text-sm text-gray-600 break-all">{docUrl || '-'}</p>
                </div>
              </div>
              {docUrl && (
                <a
                  href={docUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-md bg-wine-100 px-4 py-2 text-sm font-medium text-white hover:bg-wine-200"
                >
                  <ExternalLink className="w-4 h-4" />
                  {isExternal ? 'Open Link' : 'View File'}
                </a>
              )}
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-3">
            <h3 className="text-lg font-semibold text-gray-900 inline-flex items-center gap-2">
              <Tag className="w-4 h-4" /> Tags
            </h3>
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center rounded-md border border-violet-200 bg-violet-50 px-3 py-1 text-xs font-medium text-violet-700">
                {categoryLabel}
              </span>
              {legalFoundation.legalFoundation && (
                <span className="inline-flex items-center rounded-md border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
                  Legal Foundation
                </span>
              )}
            </div>
          </div>
        </div>

        <aside className="space-y-4 xl:sticky xl:top-24">
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
            <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
            <div className="mt-4 space-y-3 text-sm">
              <button onClick={handleEdit} className="w-full text-left text-gray-700 hover:text-gray-900 inline-flex items-center gap-2">
                <Pencil className="w-4 h-4" /> Edit LegalFoundation
              </button>
              {docUrl && (
                <a href={docUrl} target="_blank" rel="noopener noreferrer" className="w-full text-left text-gray-700 hover:text-gray-900 inline-flex items-center gap-2">
                  <ExternalLink className="w-4 h-4" /> {isExternal ? 'Open External Link' : 'Open File'}
                </a>
              )}
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
            <h3 className="text-lg font-semibold text-gray-900">Publish Information</h3>
            <div className="mt-4 space-y-3 text-sm">
              <div className="flex items-center justify-between gap-4">
                <span className="text-gray-500">Published On</span>
                <span className="text-gray-900 font-medium text-right">{formatDateTime(legalFoundation.datePublished)}</span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span className="text-gray-500">Last Updated</span>
                <span className="text-gray-900 font-medium text-right">{formatDateTime(legalFoundation.updatedAt)}</span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span className="text-gray-500">Status</span>
                <span className={`inline-flex items-center rounded-md border px-2.5 py-1 text-xs font-medium ${statusClass}`}>
                  {statusLabel}
                </span>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default LegalFoundationDetailPage;
